import { API } from '@xbto/api-client';
import { action, Action, computed, Computed, thunk, Thunk } from 'easy-peasy';
import { getDate, isBefore, subYears } from 'date-fns';
import { factories, getPdfAsBlob, runApi } from '../../utils';
import { DataModel } from '../data-store';
import { ApiThunk, Injections } from '../types';
import {
  AssetFundChartTimeRange,
  AssetFundTradingViewChartData,
  EnrichedFundFacts,
  StatusWrappedData,
  YearlyPerformances,
} from '../../types';
import { BaseModel, createBaseModel } from '../base-store';
import { ERROR_MESSAGES } from '../../constants';
import {
  getAssetBalanceHistoryChartingData,
  getFundChartingOptions,
} from '../../utils/charting-utils';
import { statusWrappedDataHelper } from '../../utils/status-wrapped-data-helper';

/**
 * TODO(@jey):
 * Although we started off this to be a generic holding model, this is very catered to fund, which is OK
 * for v2 release of prj-enhanced-funds-reporting.
 * The day we start to squeeze non-fund related props in here we may need to come up with a better store split or name
 */
export interface HoldingModel extends BaseModel {
  accountId: string | null;
  setAccountId: Action<HoldingModel, string | null>;

  // reset
  resetStore: Thunk<HoldingModel, undefined, Injections, DataModel>;

  // >> `<api>/v1/funds/get-asset-balance-history`
  fundAssetBalanceHistory: AssetFundTradingViewChartData | null;
  _setFundAssetBalanceHistory: Action<
    HoldingModel,
    AssetFundTradingViewChartData | null
  >;
  getFundAssetBalanceHistory: Thunk<
    HoldingModel,
    {
      accountId: string;
      timeRange?: AssetFundChartTimeRange;
      asset: string;
      inceptionDate?: Date;
    },
    Injections,
    DataModel,
    Promise<void>
  >;
  fundAssetBalanceHistoryTimeRanges: Computed<
    HoldingModel,
    AssetFundChartTimeRange[],
    DataModel
  >;

  // >> `<api>/fund-facts/get-monthly-performances
  fundPerformances: StatusWrappedData<YearlyPerformances>;
  setFundPerformances: Action<
    HoldingModel,
    StatusWrappedData<API.GetMonthlyFundPerformancesResponse>
  >;
  getFundPerformances: ApiThunk<
    HoldingModel,
    {
      asset: string;
      type: API.MonthlyFundPerformanceAssetType;
      accountId: string;
    },
    API.GetMonthlyFundPerformancesResponse
  >;

  // >> `<api>/fund-facts/get-fund-factsheets`
  fundFactsheets: StatusWrappedData<API.GetFactSheetsResponse>;
  latestFundFactsheet: Computed<
    HoldingModel,
    API.FactSheetItem | null,
    DataModel
  >;
  setFundFactsheets: Action<
    HoldingModel,
    StatusWrappedData<API.GetFactSheetsResponse>
  >;
  getFundFactsheets: ApiThunk<
    HoldingModel,
    { asset: string; year: number | null; accountId: string },
    API.GetFactSheetsResponse
  >;

  // >> `<api>/fund-facts/download-fact-sheet`
  getFundFactsheetDownloadUrl: ApiThunk<
    HoldingModel,
    {
      asset: string;
      month: number | null;
      year: number | null;
      accountId: string;
      downloadCallback: (blob: Blob, filename: string) => void;
    },
    API.DownloadFactSheetsResponse
  >;

  // >> `<api>/fund-facts/get-fund-facts`
  fundFacts: StatusWrappedData<EnrichedFundFacts | null>;
  setFundFacts: Action<
    HoldingModel,
    StatusWrappedData<API.GetFundFactsResponse | null>
  >;
  getFundFacts: ApiThunk<
    HoldingModel,
    { asset: string; accountId: string },
    API.GetFundFactsResponse
  >;
}

export const holdingModel: HoldingModel = {
  ...createBaseModel(),
  accountId: null,
  setAccountId: action((state, payload) => {
    state.accountId = payload;
  }),

  // reset
  resetStore: thunk(actions => {
    actions.setAccountId(null);
    actions.setFundPerformances({ data: null, errored: false });
    actions._setFundAssetBalanceHistory(null);
    actions.setFundFacts({ data: null, errored: false });
    actions.setFundFactsheets({ data: null, errored: false });
  }),

  // >> `<api>/account-balance-history/get`
  fundAssetBalanceHistory: null,
  _setFundAssetBalanceHistory: action((state, payload) => {
    state.fundAssetBalanceHistory = payload;
  }),
  getFundAssetBalanceHistory: thunk(
    async (
      actions,
      { accountId, timeRange, asset, inceptionDate },
      { injections: { apiClient }, getState }
    ) => {
      const state = getState();
      const defaultTimeRange =
        state.fundAssetBalanceHistory?.timeRange || AssetFundChartTimeRange.Y1;

      try {
        actions.setAccountId(accountId);
        actions._setFundAssetBalanceHistory(
          state.fundAssetBalanceHistory
            ? {
                ...state.fundAssetBalanceHistory,
                loading: true,
              }
            : {
                errored: false,
                asset,
                timeRange: defaultTimeRange,
                assetAmountDataPoints: null,
                usdValueDataPoints: null,
                assetAmountMarkersDataPoints: null,
                usdValueMarkersDataPoints: null,
                loading: true,
              }
        );

        // in kind graph
        apiClient.setAdditionalHeaders({
          'x-account-id': accountId,
        });
        const optionsInKind = getFundChartingOptions(
          asset,
          API.PerformanceComputationMethod.InKind,
          timeRange || defaultTimeRange,
          inceptionDate
        );
        const responseInKind =
          await apiClient.getAssetBalanceHistory(optionsInKind);

        // in usd graph
        apiClient.setAdditionalHeaders({
          'x-account-id': accountId,
        });
        const optionsInUsd = getFundChartingOptions(
          asset,
          API.PerformanceComputationMethod.Usd,
          timeRange || defaultTimeRange,
          inceptionDate
        );
        const responseUsd =
          await apiClient.getAssetBalanceHistory(optionsInUsd);

        // markers
        apiClient.setAdditionalHeaders({
          'x-account-id': accountId,
        });
        const responseMarkers = await apiClient.getBalanceHistoryMarkers({
          startDate: optionsInKind.startDate,
          endDate: optionsInKind.endDate,
          assetCode: asset,
        });

        const data = getAssetBalanceHistoryChartingData(
          asset,
          responseInKind.result,
          responseUsd.result,
          responseMarkers.result?.markers || null,
          timeRange || defaultTimeRange
        );

        actions._setFundAssetBalanceHistory(data);
      } catch (error) {
        console.error(error);
        actions._setFundAssetBalanceHistory({
          errored: true,
          asset,
          timeRange: defaultTimeRange,
          assetAmountDataPoints: null,
          usdValueDataPoints: null,
          assetAmountMarkersDataPoints: null,
          usdValueMarkersDataPoints: null,
          loading: false,
        });
      }
    }
  ),
  fundAssetBalanceHistoryTimeRanges: computed(
    [s => s.fundAssetBalanceHistory],
    fundAssetBalanceHistory => {
      if (
        !fundAssetBalanceHistory ||
        !fundAssetBalanceHistory.assetAmountDataPoints?.length
      ) {
        return [];
      }
      const isYearOld =
        isBefore(
          getDate(fundAssetBalanceHistory.assetAmountDataPoints[0].time),
          subYears(new Date(), 1)
        ) || false;
      if (!isYearOld) {
        return [];
      }
      return [
        AssetFundChartTimeRange.Y1,
        AssetFundChartTimeRange.SINCE_INCEPTION,
      ];
    }
  ),

  // >> `<api>/fund-facts/get-monthly-performances`
  fundPerformances: { data: null, errored: false },
  setFundPerformances: action((state, swd) => {
    state.fundPerformances = statusWrappedDataHelper(
      {
        ...swd,
        data: factories.enrichFundPerformances(swd.data),
      },
      state.fundPerformances
    );
  }),
  getFundPerformances: thunk(
    async (actions, { asset, type, accountId }, helpers) => {
      return runApi(
        actions,
        helpers,
        () => {
          return helpers.injections.apiClient.getMonthlyFundPerformances({
            asset,
            type,
          });
        },
        result => {
          actions.setFundPerformances({ data: result });
        },
        {
          'x-account-id':
            accountId || helpers.getStoreState().user.defaultAccountId,
        },
        {
          onLoading: loading => actions.setFundPerformances({ loading }),
          onError: errorMessage => {
            actions.setFundPerformances({ errored: !!errorMessage });
          },
        }
      );
    }
  ),

  // >> `<api>/fund-facts/get-fact-sheets`
  fundFactsheets: { data: null, errored: false },
  setFundFactsheets: action((state, swd) => {
    state.fundFactsheets = statusWrappedDataHelper(swd, state.fundFactsheets);
  }),
  getFundFactsheets: thunk(
    async (actions, { asset, year, accountId }, helpers) => {
      return runApi(
        actions,
        helpers,
        () => {
          return helpers.injections.apiClient.getFactSheets({ asset, year });
        },
        result => {
          actions.setFundFactsheets({ data: result });
        },
        {
          'x-account-id':
            accountId || helpers.getStoreState().user.defaultAccountId,
        },
        {
          onLoading: loading => actions.setFundFactsheets({ loading }),
          onError: errorMessage => {
            actions.setFundFactsheets({ errored: !!errorMessage });
          },
        }
      );
    }
  ),
  latestFundFactsheet: computed([s => s.fundFactsheets], fundFactsheets => {
    if (!fundFactsheets?.data?.factSheets?.length) {
      return null;
    }
    const latest = fundFactsheets.data.factSheets[0];
    return {
      year: latest.year,
      month: latest.month,
    };
  }),

  // >> `<api>/fund-facts/download-fact-sheet`
  getFundFactsheetDownloadUrl: thunk(
    async (
      actions,
      { asset, year, month, accountId, downloadCallback },
      helpers
    ) => {
      return runApi(
        actions,
        helpers,
        () => {
          return helpers.injections.apiClient.downloadFactSheet({
            asset,
            year,
            month,
          });
        },
        result => {
          const { fileName, url } = result;

          if (fileName && url) {
            getPdfAsBlob(url, (blob, err) => {
              if (!blob || !!err) {
                actions.setError(ERROR_MESSAGES.FACTSHEET_DOWNLOAD);
                console.error('Error downloading factsheet:', err);
                return;
              }

              downloadCallback(blob, fileName);
            });
          }
        },
        {
          'x-account-id':
            accountId || helpers.getStoreState().user.defaultAccountId,
        },
        { silent: true }
      );
    }
  ),
  // >> `<api>/fund-facts/get-fact-sheets`
  fundFacts: { data: null, errored: false },
  setFundFacts: action((state, swd) => {
    state.fundFacts = statusWrappedDataHelper(
      { ...swd, data: factories.enrichFundFacts(swd.data) },
      state.fundFacts
    );
  }),
  getFundFacts: thunk(async (actions, { asset, accountId }, helpers) => {
    return runApi(
      actions,
      helpers,
      () => {
        actions.setFundFacts({ data: null });
        return helpers.injections.apiClient.getFundFacts({ asset });
      },
      result => {
        actions.setFundFacts({ data: result });
      },
      {
        'x-account-id':
          accountId || helpers.getStoreState().user.defaultAccountId,
      },
      {
        onLoading: loading => actions.setFundFacts({ loading }),
        onError: errorMessage => {
          actions.setFundFacts({ errored: !!errorMessage, errorMessage });
        },
      }
    );
  }),
};
